/**
 * @ngdoc class
 * @name ApiCallManager
 * @description
 * This class represent a ApiCallManager object. This object contains the basic implementation
 * to get all content from the BE or get one by an Id. It also handles the cache,
 * loading status and pagination when fetching that content.
 *
 * @memberof common
 */
class ApiCallManager {
  /**
   * Class constructor.
   * @param {Service}  appUtils          The service to get the page query param.
   * @param {Function} dataProcess       The method to process the content.
   * @param {Function} methodCall        The method to fecth the content from.
   * @param {Object}   methodCallParams  The params to call the method with.
   */
  constructor(
    appUtils,
    dataProcess,
    methodCall,
    methodCallParams,
  ) {
    /**
     * The local reference to the `appUtils` service.
     * @type {Object}
     */
    this.appUtils = appUtils;
    /**
     * The local reference to the `dataProcess` function.
     * @type {Function}
     */
    this.dataProcess = dataProcess || ((content) => content);
    /**
     * The local reference to the `methodCall` function.
     * @type {String}
     */
    this.methodCall = methodCall;
    /**
     * The local reference to the `methodCallParams` object.
     * @type {Array}
     */
    this.methodCallParams = methodCallParams || [];
    /**
     * The content promise to prevent the methodCall to be called twice
     * @type {?Promise}
     */
    this.contentPromise = null;
    /**
     * The local reference to the `content` array.
     * @type {?Array}
     */
    this.content = null;
    /**
     * The pagination information.
     * @type {Object}
     * @property {Object} links the object that contains all the pagination links.
     */
    this.pagination = { links: {} };
    /**
     * Flag if a request to fetch the content is in progress.
     * @type {Boolean}
     */
    this.loading = false;
  }
  /**
   * Check if exist other content with a property value.
   * @param {Object}  content         the content with the property to check.
   * @param {String}  property        the property to check.
   * @param {Boolean} caseInsensitive if the compraison must be case-insensitive.
   * @return {Boolean}
   */
  _existContentWithProperty(content, property, caseInsensitive) {
    return !!this.content.find((item) => (
      item.id !== content.id &&
      (
        caseInsensitive ?
          item[property].toLowerCase() === content[property].toLowerCase() :
          item[property] === content[property]
      )
    ));
  }
  /**
   * Get the Content from the BE.
   * @param {Boolean} refresh flag to force a refresh of the Content.
   * @return {Promise}
   */
  _getContent(refresh) {
    if (this.contentPromise && !refresh) {
      return this.contentPromise;
    }

    this.contentPromise = this.methodCall(...this.methodCallParams)
    .then((response) => {
      this.pagination = this.appUtils.getPaginationFromResponse(response);
      this.content = this.dataProcess(response.data.data);
      return this.content;
    });

    return this.contentPromise;
  }
  /**
   * Get a Content by its Id.
   * @return {Promise}
   */
  _getContentById(contentId) {
    return this._getContent()
    .then((content) => {
      const el = content.find((item) => item.id === contentId);
      if (el) {
        // We have found the element, return it
        return el;
      } else if (this.pagination.links.next) {
        /**
         * We have not found the element,
         * but we can fetch more content and
         * search again
         */
        return this._loadMoreContent()
        .then(() => this._getContentById(contentId));
      }

      // We have not found the element, return an error
      return Promise.reject(new Error('Content could not be found'));
    });
  }
  /**
   * Load more Content from the BE.
   * @return {Promise}
   */
  _loadMoreContent() {
    return this._getContent()
    .then((existentContent) => {
      const page = this.appUtils.getQueryParam(this.pagination.links.next, 'page');

      this.contentPromise = this.methodCall(...this.methodCallParams, page)
      .then((response) => {
        this.pagination = this.appUtils.getPaginationFromResponse(response);
        this.content = [
          ...existentContent,
          ...this.dataProcess(response.data.data),
        ];
        return this.content;
      });
      return this.contentPromise;
    });
  }
  /**
   * Add content to the content array.
   * @param {Object} newContent the content to add.
   */
  addContent(newContent) {
    const el = this.content.find((item) => item.id === newContent.id);
    if (!el) {
      this.content.push(newContent);
    }
  }
  /**
   * Handle the load of the content.
   * @param {Boolean} first flag to get the Content
   *                  for the first time or load more.
   * @return {Promise}
   */
  load(first) {
    this.loading = true;
    const promise = first
      ? this._getContent()
      : this._loadMoreContent();

    return promise.then((content) => {
      this.loading = false;
      return content;
    });
  }
  /**
   * Handle the pagination has more data status.
   */
  hasMoreData() {
    return !this.loading && this.pagination.links.next;
  }
  /**
   * Handle the clear of the saved content.
   */
  clearContent() {
    this.contentPromise = null;
  }
  /**
   * Remove content from the content array.
   * @param {Object} oldContent the content to remove.
   */
  removeContent(oldContent) {
    const index = this.content.findIndex((item) => item.id === oldContent.id);
    if (index > -1) {
      this.content.splice(index, 1);
    }
  }
  /**
   * Find the updatedContent by id in the content array
   * and merge the oldContent with the updatedContent.
   * @param {Object}   updatedContent   the content to update.
   * @param {Funciton} preUpdateProcess the function to call before merging the content.
   */
  updateContent(updatedContent, preUpdateProcess) {
    const oldContent = this.content.find((item) => item.id === updatedContent.id);
    if (oldContent) {
      if (preUpdateProcess) {
        preUpdateProcess(oldContent);
      }
      angular.merge(oldContent, updatedContent);
    }
  }
}

export default ApiCallManager;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/models/apiCallManager.js